import type { Entity, Structure, Unit } from "model/OrganizationalStructure";
import type { StaffItem } from "services/api/company/staff.service";

export type OrganizationPerson = StaffItem;

export interface OrganizationUnit extends Unit {
  readonly entityId: number;
}

interface FilterWorkersParams {
  readonly query: string;
  readonly workers: readonly OrganizationPerson[];
}

interface FilterOrganizationUnitsParams {
  readonly selectedTeamIds: readonly string[];
  readonly units: readonly OrganizationUnit[];
}

interface GetUnitTeamCountParams {
  readonly entityId: number;
  readonly unitId: number;
  readonly units: readonly OrganizationUnit[];
}

interface HasOtherTeamMembershipParams {
  readonly currentTeamId: number;
  readonly worker: OrganizationPerson;
}

export function filterOrganizationUnits({
  selectedTeamIds,
  units,
}: FilterOrganizationUnitsParams): OrganizationUnit[] {
  if (selectedTeamIds.length === 0) return [...units];
  const selectedTeamIdSet = new Set(selectedTeamIds);

  return units.flatMap((unit) => {
    const matchingTeams = unit.workGroups.filter((team) =>
      selectedTeamIdSet.has(String(team.id)),
    );

    return matchingTeams.length > 0
      ? [{ ...unit, workGroups: matchingTeams }]
      : [];
  });
}

export function flattenOrganizationUnits(
  structure: Structure,
): OrganizationUnit[] {
  return structure.entities.flatMap((entity: Entity) => {
    return entity.units.map((unit: Unit) => ({
      ...unit,
      entityId: entity.id,
    }));
  });
}

export function getUnitPersonCount(unit: Unit): number {
  const workerIds = unit.workGroups.flatMap((team) =>
    (team.workers ?? []).map((worker) => worker.workerId),
  );
  return new Set(workerIds).size;
}

export function getUnitTeamCount({
  entityId,
  unitId,
  units,
}: GetUnitTeamCountParams): number {
  return (
    units.find((unit) => unit.entityId === entityId && unit.id === unitId)
      ?.workGroups.length ?? 0
  );
}

export function getWorkerName(worker: OrganizationPerson): string {
  return `${worker.firstName ?? ""} ${worker.lastName ?? ""}`.trim();
}

export function getWorkerRole(worker: OrganizationPerson): string {
  return worker.role ?? "";
}

export function isWorkerInactive(worker: OrganizationPerson): boolean {
  return worker.status !== "Active";
}

export function hasOtherTeamMembership({
  currentTeamId,
  worker,
}: HasOtherTeamMembershipParams): boolean {
  return worker.workGroups.some(
    (workGroup) => workGroup.workGroupId !== currentTeamId,
  );
}

export function mergeWorkersById(
  current: readonly OrganizationPerson[],
  next: readonly OrganizationPerson[],
): OrganizationPerson[] {
  return Array.from(
    new Map(
      [...current, ...next].map((worker) => [worker.workerId, worker]),
    ).values(),
  );
}

export function filterWorkers({
  query,
  workers,
}: FilterWorkersParams): OrganizationPerson[] {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  if (!normalizedQuery) return [...workers];

  return workers.filter((worker) => {
    const teamNames = worker.workGroups
      .map((team) => team.workGroupName ?? "")
      .join(" ");
    const unitNames = worker.workGroups
      .map((team) => team.companyUnitName ?? "")
      .join(" ");
    const searchable = `${getWorkerName(worker)} ${getWorkerRole(worker)} ${teamNames} ${unitNames}`;
    return searchable.toLocaleLowerCase().includes(normalizedQuery);
  });
}
